import { Dropdown } from "@/dropdown";
import { AbstractFormControl } from "./form-control";

// Note: we don't store a reference to the radio inputs since they might change dynamically
export class RadioGroupControl extends AbstractFormControl<HTMLElement> {

    /** Radio groups are typically not deselectable, but sometimes this feature is needed if a user selects an option accidentally, when the proper selection is not yet known */
    static values = {'deselectable': Boolean};

    getName(): string {
        return this.element.dataset.name;
    }

    getSelectedOption(): HTMLInputElement | null {
        return this.element.querySelector('input:checked')
    }

    getValue() {
        let selected = this.getSelectedOption();
        return selected ? selected.value : null;
    }

    setValue(value: any) {
        // Check if value changed
        let selected = this.getSelectedOption();
        if (selected && selected.value == value) {
            return;
        }

        // Uncheck previously selected option
        if (selected) {
            selected.checked = false;
        }

        // Set new option checked
        let option = <HTMLInputElement>this.element.querySelector(`input[value='${value}']`);
        if (option) {
            option.checked = true;
        }
    }

    getOption(id: string): RadioGroupOption | null {
        let inputEl = this.element.querySelector(`input[value='${id}']`);
        return !inputEl ? null : new RadioGroupOption(
            inputEl.parentElement,
            <HTMLInputElement>inputEl,
            <HTMLLabelElement>inputEl.nextSibling
        );
    }

    addOption(id: string, name: string, alphabetical = true) {
        let group = this.getName();

        let input = document.createElement('input');
        input.id = group + '_' + name;
        input.type = 'radio';
        input.name = group;
        input.value = id;

        let label = document.createElement('label');
        label.htmlFor = input.id;
        label.innerText = name;

        let newOption = document.createElement('div');
        newOption.classList.add('ww-radio__option');
        newOption.appendChild(input);
        newOption.appendChild(label);

        let nextOption = alphabetical ? this.getNextAlphabeticalOption(name) : null;
        this.element.insertBefore(newOption, nextOption);
    }

    private getNextAlphabeticalOption(name: string) {
        return Dropdown.getNextAlphabeticalItem(
            name, this.element.childNodes, optionEl => {
                let label = <HTMLElement>optionEl.childNodes[1];
                return label.innerText
            }
        );
    }

    removeOption(id: string) {
        let option = this.getOption(id);
        if (option) {
            option.el.remove();
        }
    }

    updateOption(id: string, name: string, alphabetical = true) {
        this.removeOption(id);
        this.addOption(id, name, alphabetical);
    }

    deselectableValueChanged(deselectable: boolean) {
        if (deselectable) {
            let previousValue = this.getValue();
            this.element.querySelectorAll('input').forEach(input => {
                input.addEventListener('change', (e) => {
                    previousValue = input.value;
                })
                input.addEventListener('click', (e) => {
                    if (input.value === previousValue) { // Clicked on input that is already selected
                        input.checked = false;
                        previousValue = null;
                        this.element.dispatchEvent(new Event('change'));
                    }
                });
            });
        }
    }
}

export class RadioGroupOption {
    public constructor(
        public readonly el: HTMLElement,
        private inputEl: HTMLInputElement,
        private labelEl: HTMLLabelElement
    ) { }

    public getId() {
        return this.inputEl.value;
    }

    public getName() {
        return this.labelEl.innerText;
    }

    public setName(name: string) {
        this.labelEl.innerText = name;
    }
}
